import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "../../lib/api";
import { DNC_REASONS, fmtDT, inputCls } from "../../lib/outreach";

const EMPTY = { email: "", domain: "", company_name: "", reason: "Excluded" };

export default function DncManager({ isAdmin }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");

  const load = () => api.get("/dnc").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/dnc", form);
      toast.success("Added to do-not-contact list");
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this entry? They could be contacted again.")) return;
    try { await api.delete(`/dnc/${id}`); load(); } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const shown = items.filter((i) => `${i.email} ${i.domain} ${i.company_name}`.toLowerCase().includes(q.toLowerCase()));
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div data-testid="dnc-manager">
      {isAdmin && (
        <form onSubmit={add} className="rounded-xl border border-slate-800 bg-[#131C31] p-4 mb-4 grid grid-cols-1 sm:grid-cols-5 gap-3 items-end">
          <input data-testid="dnc-email-input" placeholder="Email" className={inputCls} value={form.email} onChange={set("email")} />
          <input data-testid="dnc-domain-input" placeholder="Domain (e.g. acme.com)" className={inputCls} value={form.domain} onChange={set("domain")} />
          <input data-testid="dnc-company-input" placeholder="Company name" className={inputCls} value={form.company_name} onChange={set("company_name")} />
          <select data-testid="dnc-reason-select" className={inputCls} value={form.reason} onChange={set("reason")}>
            {DNC_REASONS.map((r) => <option key={r}>{r}</option>)}
          </select>
          <button data-testid="dnc-add-button" disabled={busy} className="btn-primary rounded-lg px-4 py-2 text-sm inline-flex items-center justify-center gap-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add
          </button>
        </form>
      )}
      <input data-testid="dnc-search-input" placeholder="Search do-not-contact list…" className={`${inputCls} mb-3 max-w-sm`} value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="rounded-xl border border-slate-800 bg-[#131C31] overflow-x-auto">
        <table className="w-full text-sm min-w-[680px]">
          <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-800">
            <tr><th className="p-3 text-left">Email</th><th className="p-3 text-left">Domain</th><th className="p-3 text-left">Company</th><th className="p-3 text-left">Reason</th><th className="p-3 text-left">Added</th><th className="p-3" /></tr>
          </thead>
          <tbody>
            {shown.length === 0 ? (
              <tr><td data-testid="dnc-empty" colSpan={6} className="p-8 text-center text-slate-500">No entries.</td></tr>
            ) : shown.map((i) => (
              <tr key={i.id} data-testid={`dnc-row-${i.id}`} className="border-b border-slate-800/60">
                <td className="p-3 text-slate-200">{i.email || "—"}</td>
                <td className="p-3 text-slate-300">{i.domain || "—"}</td>
                <td className="p-3 text-slate-300">{i.company_name || "—"}</td>
                <td className="p-3 text-xs text-amber-300">{i.reason}</td>
                <td className="p-3 text-xs text-slate-400">{fmtDT(i.created_at)}</td>
                <td className="p-3 text-right">
                  {isAdmin && <button data-testid={`dnc-delete-${i.id}`} onClick={() => remove(i.id)} className="text-slate-500 hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
